import React, { FC, useMemo } from "react";
import { IdleDetailingItem, WorkDisplayPeriod } from "@baseTypes/service/dashboard/IWorksDashboardService";
import * as Highcharts from "highcharts";
import { applyTip, wideFontSize18, wideFontSize20 } from "@utils/utils";
import LinearChart from "../../../../../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import { flatMap, isUndefined } from "lodash";
import 'moment/locale/ru';
import {
    formatTime,
    hoursAndMinutesTimeFormatter
} from "@utils/format/time";
import { onHighlightSeries, onCancelHighlightSeries } from "@pages/dashboard/expenses/articles";
import { positionerWithOffset } from "@utils/highchart/tooltip";
import {
    useIdlesChartValues
} from "../../hooks/useIdlesChartValues";
import IdlesTooltip from "../IdlesTooltip/IdlesTooltip";
import { IdleTypes } from "../../index";
import {
    getChartSummary,
    getShortMonthWithDay,
    labelFormatter
} from "../../utils/utils";
import Scrollbar from "../../../../../../../../utils/highchart/Scrollbar";
import PlanFactLegend from "@elements/planFactLegend/PlanFactLegend";
import { Align } from "@baseTypes/baseType";
import useDashboardPageSize, { PageSize } from "@hooks/useDashboardPageSize";

type IdlesStackedBarsChartProps = {
    period: WorkDisplayPeriod
    elements: IdleDetailingItem[]
}

const PLANED_POINT_START = -0.2
const UNPLANNED_POINT_START = 0.2

const chartWidth: Record<WorkDisplayPeriod, Record<PageSize, number>> = {
    full: {
        small: 390,
        middle: 820,
        default: 1245
    },
    today: {
        small: 380,
        middle: 300,
        default: 450
    }
}

const IdlesStackedBarsChart: FC<IdlesStackedBarsChartProps> = (props) => {
    const {
        elements,
        period
    } = props;

    const planedIdles = useIdlesChartValues('plannedIdlesByType', elements)
    const unplannedIdles = useIdlesChartValues('unplannedIdlesByType', elements)
    const isToday = period === 'today';

    const dashboardSize = useDashboardPageSize()

    const maxElementsInChart = dashboardSize === 'small' ? 4 : 12

    const defaultOptions = useMemo(() => {
        const plannedSummary = getChartSummary(elements, 'plannedIdlesByType')
        const unplannedSummary = getChartSummary(elements, 'unplannedIdlesByType')
        const isFull = period === 'full';

        const isLengthExceededThreshold = elements.length >= maxElementsInChart

        const defaultOptions: Highcharts.Options = {
            chart: {
                width: chartWidth[period][dashboardSize],
                height: 360,
                animation: false,
                marginLeft: 0
            },
            title: {
                text: ''
            },
            credits: {
                enabled: false
            },
            legend: {
                enabled: false
            },
            tooltip: applyTip((ctx, tooltip) => {
                const dateIndex = tooltip.chart.hoverPoint?.index
                const planed = ctx.series.name === IdleTypes.planed

                if (isUndefined(dateIndex) || !elements.at(dateIndex)) return <></>

                const dataToRender = elements[dateIndex][planed ? 'plannedIdlesByType' : 'unplannedIdlesByType']

                return <IdlesTooltip dataToRender={dataToRender} />
            }, {
                shared: false,
                positioner: positionerWithOffset({ x: 0, y: 10 })
            }),
            yAxis: {
                type: "datetime",
                tickAmount: isToday ? 10 : undefined,
                max:  (Math.max(...flatMap(plannedSummary), ...flatMap(unplannedSummary)) || 0) * 1.2,
                min: 0,
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
                labels: {
                    style: {
                        fontSize: wideFontSize20()
                    },
                    formatter: (t) => formatTime(t.value as number, hoursAndMinutesTimeFormatter)
                },
                gridLineWidth: 1,
                showEmpty: true,
            },
            accessibility: {
                announceNewData: {
                    enabled: true
                }
            },
            xAxis: {
                categories: getShortMonthWithDay(elements.map(element => element.date)),
                labels: {
                    style: {
                        fontSize: wideFontSize20(),
                        color: 'rgba(85, 87, 89, 1)'
                    },
                    align: 'center',
                    autoRotation: undefined,
                    enabled: isFull
                },
                gridLineWidth: isFull ? 1 : 0,
                endOnTick: isFull,
                max: isFull ? Math.min(elements.length - 1, maxElementsInChart - 1) : 0,
                startOnTick: isFull,
                min: isFull ? 0 : null,
                scrollbar: Scrollbar({ enabled: isLengthExceededThreshold }),
                lineColor: 'rgba(205, 206, 209, 1)',
                tickColor: 'rgba(205, 206, 209, 1)'
            },

            plotOptions: {
                series: {
                    opacity: 1,
                    pointInterval: 1,
                    allowPointSelect: true,
                    marker: {
                        enabled: false
                    },

                    events: {
                        legendItemClick: function () {
                            return false;
                        },
                        click: function () {
                            return false;
                        }
                    }
                },
                column: {
                    grouping: false,
                    point: {
                        events: {
                            mouseOver: onHighlightSeries,
                            mouseOut: onCancelHighlightSeries
                        }
                    },
                    states: {
                        inactive: {
                            opacity: 1
                        },
                        hover: {
                            enabled: true
                        }
                    },
                    stacking: 'normal',
                    groupPadding: 0.7,
                    pointPadding: 0.1,
                    pointWidth: 20,
                }
            },
            series: [
                {
                    name: IdleTypes.planed,
                    pointWidth: isToday ? 64 : 25,
                    stacking: undefined,
                    pointStart: PLANED_POINT_START,
                    type: 'column',
                    color: "rgba(134, 206, 82, 0.4)",
                    data: plannedSummary,
                    visible: true,
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            const label = labelFormatter(this, plannedSummary, unplannedSummary)

                            if (!isUndefined(label) && label.displayInPlanned) return label.summary
                        },
                        style: {
                            color: '#696B6E',
                            fontSize: isToday ? wideFontSize20() : wideFontSize18(),
                            fontWeight: 400
                        },
                        verticalAlign: 'bottom',
                        useHTML: true,
                        x: isToday ? 50 : isLengthExceededThreshold ? 10 : 30,
                    }
                },
                {
                    name: IdleTypes.unplanned,
                    pointWidth: isToday ? 64 : 25,
                    stacking: undefined,
                    pointStart: UNPLANNED_POINT_START,
                    type: 'column',
                    color: "rgba(254, 124, 102, 0.4)",
                    data: unplannedSummary,
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            const label = labelFormatter(this, plannedSummary, unplannedSummary)

                            if (!isUndefined(label) && !label.displayInPlanned) return label.summary
                        },
                        style: {
                            color: '#696B6E',
                            fontSize: isToday ? wideFontSize20() : wideFontSize18(),
                            fontWeight: 400,
                        },
                        verticalAlign: 'bottom',
                        useHTML: true,
                        x: isToday ? -50 : isLengthExceededThreshold ? -10 : -30
                    }
                },

                ...(planedIdles.map(idle => ({
                    name: IdleTypes.planed,
                    pointWidth: isToday ? 40 : 10,
                    stack: 'П',
                    pointStart: PLANED_POINT_START,
                    showInLegend: false,
                    type: 'column',
                    ...idle
                })) as any),

                ...(unplannedIdles.map(idle => ({
                    name: IdleTypes.unplanned,
                    pointWidth: isToday ? 40 : 10,
                    stack: 'Ф',
                    pointStart: UNPLANNED_POINT_START,
                    showInLegend: false,
                    type: 'column',
                    ...idle
                })) as any),
            ]
        }

        return defaultOptions
    }, [elements, period, planedIdles, unplannedIdles, isToday, dashboardSize, maxElementsInChart])

    return (
        <div>
            <div>
                <LinearChart options={defaultOptions} isStock />
            </div>
            <PlanFactLegend padding={{ right: isToday ? 0 : 10, left: isToday ? 10 : 0 }}
                            unselectable
                            planName={'Плановые простои'}
                            factName={'Внеплановые простои'}
                            align={isToday ? Align.Start : Align.End}
            />
        </div>
    )
}

export default IdlesStackedBarsChart